import React, { useRef, useState, useEffect, CSSProperties } from "react";
import "~/css/ChatHistory.css";
import {
  messagesAtom,
  currentVRMAtom,
  volumeAtom,
  isListeningAtom,
  layoutSelectAtom,
} from "~/Atom";
import { listeningModeAtom } from "~/AtomWithDynamoDB";
import ChatMessage from "./ChatMessage";
import { useAtom } from "jotai";
import { PlaySound } from "~/SoundUtils";
import PulseLoader from "react-spinners/PulseLoader";
import ChatHistoryLayout1 from "~/components/layout/ChatHistoryLayout1";
import ChatHistoryLayout2 from "~/components/layout/ChatHistoryLayout2";
import ChatHistoryLayout3 from "~/components/layout/ChatHistoryLayout3";

// PulseLoader用の定数
const pulseLoaderColor = "#4da8cb";
const pulseLoaderSize = 25;

// サウンド再生用の音量係数
const messageSoundVolumeFactor = 1.0;
const scrollSoundVolumeFactor = 0.4;

const ChatHistory: React.FC = () => {
  const [messages] = useAtom(messagesAtom);
  const [currentVRM] = useAtom(currentVRMAtom);
  const chatHistoryRef = useRef<HTMLDivElement | null>(null);
  const [showScrollToTopButton, setShowScrollToTopButton] = useState(false);
  const [volume] = useAtom(volumeAtom);
  const [isListening] = useAtom(isListeningAtom);
  const previousMessagesRef = useRef(JSON.stringify(messages));
  const [layoutSelect] = useAtom(layoutSelectAtom);
  const [listeningMode] = useAtom(listeningModeAtom);

  // scroll-to-top-buttonのbottom位置(px)
  const scrollButtonBottomDefault = 20;
  const scrollButtonBottomButtonModeDefault = 70;
  const scrollButtonBottomButtonModeLayout1 = 70;
  const scrollButtonBottomButtonModeLayout2 = 45;
  const scrollButtonBottomButtonModeLayout3 = 90;

  // ボタン押下時聞き取りモード(listeningMode===2)時のscroll-to-top-buttonのbottom値を算出
  const getScrollButtonBottom = (): number => {
    if (listeningMode !== 2) return scrollButtonBottomDefault;
    switch (layoutSelect) {
      case 1:
        return scrollButtonBottomButtonModeLayout1;
      case 2:
        return scrollButtonBottomButtonModeLayout2;
      case 3:
        return scrollButtonBottomButtonModeLayout3;
      default:
        return scrollButtonBottomButtonModeDefault;
    }
  };
  const scrollButtonBottom = getScrollButtonBottom();

  // 初回メッセージ追加処理
  useEffect(() => {
    // キャラクター変更時に特定の処理が必要な場合はここに実装
  }, [currentVRM]);

  // メッセージが追加されたときの処理
  useEffect(() => {
    // メッセージ追加時に自動的に最下部までスクロール
    scrollToBottom();
    // メッセージの内容が変更された場合のみ音を再生
    const currentMessagesString = JSON.stringify(messages);
    if (currentMessagesString !== previousMessagesRef.current) {
      PlaySound("message", messageSoundVolumeFactor * volume);
      previousMessagesRef.current = currentMessagesString;
    }

    // 初期化時にスクロール位置に応じてボタンを表示
    handleScroll();

    // スクロールイベントリスナーを設定
    const chatHistory = chatHistoryRef.current;
    if (chatHistory) {
      chatHistory.addEventListener("scroll", handleScroll);
    }

    // クリーンアップ関数：コンポーネントアンマウント時にイベントリスナーを削除
    return () => {
      if (chatHistory) {
        chatHistory.removeEventListener("scroll", handleScroll);
      }
    };
  }, [messages, volume]);

  // DOM変更を監視し自動スクロール
  useEffect(() => {
    const chatHistory = chatHistoryRef.current;
    if (!chatHistory) return undefined;

    const observer = new MutationObserver(() => {
      scrollToBottom();
    });

    observer.observe(chatHistory, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  // スクロール位置に応じて戻るボタンの表示を制御する
  const handleScroll = () => {
    if (chatHistoryRef.current) {
      setShowScrollToTopButton(chatHistoryRef.current.scrollTop > 0);
    }
  };

  // チャット履歴を最下部までスクロールする
  const scrollToBottom = () => {
    if (chatHistoryRef.current) {
      const scrollHeight = chatHistoryRef.current.scrollHeight;
      const scrollTop = chatHistoryRef.current.scrollTop;
      const clientHeight = chatHistoryRef.current.clientHeight;
      const isScrolledToBottom = scrollHeight - scrollTop === clientHeight;

      if (!isScrolledToBottom) {
        chatHistoryRef.current.scrollTo({
          top: scrollHeight,
          behavior: "smooth",
        });
      }
    }
  };

  // チャット履歴を最上部までスクロールする
  const scrollToTop = () => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  // PulseLoader用のスタイル設定
  const override: CSSProperties = {
    display: "block",
    margin: "10px auto",
  };

  switch (layoutSelect) {
    case 1:
      return (
        <ChatHistoryLayout1
          messages={messages}
          chatHistoryRef={chatHistoryRef}
          showScrollToTopButton={showScrollToTopButton}
          scrollToTop={scrollToTop}
          scrollToBottom={scrollToBottom}
          isListening={isListening}
          override={override}
          volume={volume}
          scrollButtonBottom={scrollButtonBottom}
        />
      );
    case 2:
      return (
        <ChatHistoryLayout2
          messages={messages}
          chatHistoryRef={chatHistoryRef}
          showScrollToTopButton={showScrollToTopButton}
          scrollToTop={scrollToTop}
          scrollToBottom={scrollToBottom}
          isListening={isListening}
          override={override}
          volume={volume}
          scrollButtonBottom={scrollButtonBottom}
        />
      );
    case 3:
      return (
        <ChatHistoryLayout3
          messages={messages}
          chatHistoryRef={chatHistoryRef}
          showScrollToTopButton={showScrollToTopButton}
          scrollToTop={scrollToTop}
          scrollToBottom={scrollToBottom}
          isListening={isListening}
          override={override}
          volume={volume}
          scrollButtonBottom={scrollButtonBottom}
          isButtonMode={listeningMode === 2}
        />
      );

    default:
      return (
        <>
          <div className="chat-history" ref={chatHistoryRef}>
            {messages.map((message, index) => (
              <div key={index} className="message">
                <ChatMessage
                  key={index}
                  role={message.role}
                  content={message.content}
                  onImageLoad={scrollToBottom}
                  messageId={message.messageId || ""}
                  contentColor={message.contentColor}
                />
              </div>
            ))}

            <PulseLoader
              color={pulseLoaderColor}
              loading={!isListening}
              cssOverride={override}
              size={pulseLoaderSize}
            />

            <button
              className={`btn btn-secondary btn-circle scroll-to-top-button ${showScrollToTopButton ? "show" : ""}`}
              onClick={showScrollToTopButton ? scrollToTop : undefined}
              onMouseUp={() => PlaySound("pifo", scrollSoundVolumeFactor * volume)}
              style={{ bottom: `${scrollButtonBottom}px` }}
            >
              ⬆
            </button>
          </div>
        </>
      );
  }
};

export default ChatHistory;
